import { useEffect, useRef, useState } from "react";
import { cn } from "@/utils/cn";
import { playPoke } from "./tutor-sounds";

export type MascotMood = "idle" | "thinking" | "talking" | "happy" | "sad";

const INK = "#2b2622";
const CHEEK = "#ffb4c2";

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The AI tutor's mascot: a round, sticker-style speech bubble with a little graduation cap. Pure SVG; the
 * body motion is CSS keyframes (globals.css, `.tm-*`), the life-like bits are timers:
 *
 * - blinks at random intervals (sometimes twice), and now and then glances around
 * - `interactive`: the eyes follow the pointer, and a tap squashes it with a giggle (+ a "boop")
 * - moods: idle, thinking (tilts, dots — while the AI writes), talking (mouth moves — an answer just
 *   arrived), happy (hops, ^^ eyes, hearts), sad (tear — an error)
 *
 * Everything stops for visitors who prefer reduced motion.
 */
export function TutorMascot({
  mood = "idle",
  size = 48,
  animated = true,
  interactive = false,
  className,
}: {
  mood?: MascotMood;
  size?: number;
  animated?: boolean;
  interactive?: boolean;
  className?: string;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const [blink, setBlink] = useState(false);
  const [look, setLook] = useState({ x: 0, y: 0 });
  const [poked, setPoked] = useState(false);
  const live = animated && !prefersReducedMotion();

  // Random blinks: every 2–6 s, one in four is a double blink.
  useEffect(() => {
    if (!live) return;
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(() => {
        setBlink(true);
        window.setTimeout(() => setBlink(false), 130);
        if (Math.random() < 0.25) {
          window.setTimeout(() => setBlink(true), 260);
          window.setTimeout(() => setBlink(false), 390);
        }
        schedule();
      }, 2000 + Math.random() * 4000);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [live]);

  // Idle glances: every 6–12 s look somewhere for a moment (unless following the pointer).
  useEffect(() => {
    if (!live || interactive) return;
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(() => {
        setLook({ x: (Math.random() * 2 - 1) * 3, y: (Math.random() * 2 - 1) * 1.5 });
        window.setTimeout(() => setLook({ x: 0, y: 0 }), 900 + Math.random() * 700);
        schedule();
      }, 6000 + Math.random() * 6000);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [live, interactive]);

  // Eyes follow the pointer (mouse or finger).
  useEffect(() => {
    if (!live || !interactive) return;
    const onMove = (event: PointerEvent) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box) return;
      const dx = event.clientX - (box.left + box.width / 2);
      const dy = event.clientY - (box.top + box.height / 2);
      const distance = Math.hypot(dx, dy) || 1;
      const reach = Math.min(1, distance / 260);
      setLook({ x: (dx / distance) * 3.2 * reach, y: (dy / distance) * 2.4 * reach });
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [live, interactive]);

  function poke() {
    if (!interactive) return;
    playPoke();
    setPoked(true);
    window.setTimeout(() => setPoked(false), 700);
  }

  const shown: MascotMood = poked ? "happy" : mood;
  const up = shown === "thinking" ? -4 : shown === "sad" ? 3 : 0;
  const right = shown === "thinking" ? 2 : 0;

  return (
    <svg
      ref={ref}
      viewBox="0 0 120 120"
      width={size}
      height={size}
      aria-hidden="true"
      onClick={poke}
      className={cn(
        "tm-root shrink-0 overflow-visible",
        live && `tm-${shown}`,
        poked && "tm-poked",
        interactive && "cursor-pointer select-none",
        className,
      )}
    >
      <ellipse className="tm-shadow" cx="60" cy="112" rx="30" ry="4.5" fill={INK} opacity="0.12" />

      <g className="tm-body">
        <path
          d="M60 26 C88 26 104 43 104 64 C104 87 86 103 60 103 C53 103 46 102 40 99 L24 107 L29 93 C21 85 16 76 16 64 C16 43 32 26 60 26 Z"
          fill="#ffffff"
          stroke={INK}
          strokeWidth="4.5"
          strokeLinejoin="round"
        />

        <g className="tm-cap">
          <path d="M60 10 L84 20 L60 30 L36 20 Z" fill={INK} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
          <path d="M46 24 L46 31 C52 35 68 35 74 31 L74 24" fill={INK} />
          <g className="tm-tassel">
            <path d="M84 20 L84 33" stroke="#f5c518" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="84" cy="35" r="3" fill="#f5c518" />
          </g>
        </g>

        <ellipse className="tm-cheek" cx="37" cy="72" rx="7.5" ry="4.8" fill={CHEEK} />
        <ellipse className="tm-cheek" cx="83" cy="72" rx="7.5" ry="4.8" fill={CHEEK} />

        <g className="tm-eyes" style={{ transform: `translate(${look.x}px, ${look.y}px)`, transition: "transform 160ms ease-out" }}>
          {shown === "happy" ? (
            <>
              <path d="M40 61 Q46 53 52 61" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
              <path d="M68 61 Q74 53 80 61" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
            </>
          ) : (
            <>
              {shown === "sad" && (
                <>
                  <path d="M40 57 L50 53" stroke={INK} strokeWidth="3" strokeLinecap="round" />
                  <path d="M80 57 L70 53" stroke={INK} strokeWidth="3" strokeLinecap="round" />
                </>
              )}
              <g className="tm-blink" style={{ transform: blink ? "scaleY(0.1)" : undefined, transition: "transform 60ms" }}>
                <circle cx={46 + right} cy={61 + up} r="4.8" fill={INK} />
                <circle cx={74 + right} cy={61 + up} r="4.8" fill={INK} />
                <circle cx={47.5 + right} cy={59.5 + up} r="1.5" fill="#fff" />
                <circle cx={75.5 + right} cy={59.5 + up} r="1.5" fill="#fff" />
              </g>
            </>
          )}
        </g>

        {shown === "happy" ? (
          <path d="M51 71 Q60 84 69 71 Z" fill={INK} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
        ) : shown === "sad" ? (
          <path d="M53 78 Q60 71 67 78" fill="none" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />
        ) : shown === "thinking" ? (
          <ellipse cx="62" cy="75" rx="3.5" ry="3" fill={INK} />
        ) : shown === "talking" ? (
          <ellipse className="tm-talk" cx="60" cy="75" rx="6" ry="4.5" fill={INK} />
        ) : (
          <path d="M53 72 Q60 79 67 72" fill="none" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />
        )}

        {shown === "sad" && <path className="tm-tear" d="M84 66 C86 70 88 73 86 76 C84 78 81 76 82 73 C82 71 83 69 84 66 Z" fill="#7cc4f2" />}
      </g>

      {shown === "thinking" && (
        <g className="tm-dots" fill={INK}>
          <circle cx="94" cy="22" r="3.4" />
          <circle cx="103" cy="15" r="3.4" />
          <circle cx="111" cy="9" r="3.4" />
        </g>
      )}

      {shown === "happy" && (
        <g className="tm-hearts" fill="#ff7b9c">
          <path d="M14 30 c-3-4 -9-1 -6 4 l6 6 l6-6 c3-5 -3-8 -6-4 Z" />
          <path d="M104 22 c-2.4-3.2 -7.2-0.8 -4.8 3.2 l4.8 4.8 l4.8-4.8 c2.4-4 -2.4-6.4 -4.8-3.2 Z" />
        </g>
      )}
    </svg>
  );
}
